import React, { useState, useEffect, Suspense, useContext, useRef } from 'react';
import './IdeallineApp.css';
import './Loading.css';
import { useRouteMatch, useHistory} from "react-router-dom";
const LineTool = React.lazy(() => import('./LineTool'));
import List from './List'
import SubscriptionWall from '../subscription/SubscriptionWall'
import usePrevious from './usePrevious'

import { FirebaseContext } from './Firebase'
import { lineSource } from '../../utils/adminDashboard'
import { PADDOCK_PASS_REQUIRED_MESSAGE, requireRacingLineEntitlement } from '../../utils/racingLineEntitlement'
import { createFreeManualRacingLine, publishClosedRacingLines } from '../../utils/manualLineCreation'
import { createEditorTools, registerWebMcpTools } from '../../utils/racingLineWebMcp'
import WebMcpInfo from './WebMcpInfo'
import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/analytics'
import 'firebase/functions'
import 'firebase/storage'

const App = (props) => {
  const [isTool, setIsTool] = useState(false);
  const [lineId, setLineId] = useState(null);
  const match = useRouteMatch("*/r/:lineId");
  const [ignoreMatch, setIgnoreMatch] = useState(false);
  const history = useHistory();
  const prevMatch = usePrevious(match)
  const [editMode, setEditMode] = useState(false);

  // History stacks for undo/redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Track states
  const trackDefaults = {
    points: [],
    lat: props.lat,
    lng: props.lng,
    zoom: props.zoom || 19,
    visibility: "public",
    vehicle: ""
  }
  const [track, setTrack] = useState(trackDefaults)
  const [zoom, setZoom] = useState(track.zoom)
  const [errorMessage, setErrorMessage] = useState(null)
  const [showSubscriptionWall, setShowSubscriptionWall] = useState(false)
  const [center, setCenter] = useState({lat: track.lat, lng: track.lng});
  const [isCloseDetected, setIsCloseDetected] = useState(false);
  const [subscriptionContext, setSubscriptionContext] = useState({ title: null, message: null, intent: 'unknown' })
  const [showAccountPrompt, setShowAccountPrompt] = useState(false)
  const [webMcpReady, setWebMcpReady] = useState(false)
  const editorStateRef = useRef(null)
  const editorActionsRef = useRef(null)

  const showCreationGate = (action = 'create or import racing lines', intent = 'unknown') => {
    setSubscriptionContext({
      title: 'Paddock Pass required',
      message: `${PADDOCK_PASS_REQUIRED_MESSAGE} Subscribe to ${action}. Your subscription starts immediately.`,
      intent
    })
    setShowSubscriptionWall(true)
  }

  const requireCreationEntitlement = (onAllowed, action, intent) => requireRacingLineEntitlement({
    auth,
    onAllowed,
    onDenied: () => showCreationGate(action, intent)
  })

  
  // Firebase 
  const auth = useContext(FirebaseContext)
  const firebase = auth.app
  const linesDb = firebase.firestore().collection(`lines`)
  const user = auth.user
  const {requireAuth} = auth
  const signInAnonymouslyAndGetUser = auth.signInAnonymouslyAndGetUser

  editorStateRef.current = {
    lineId,
    trackName: props.name || track.name || '',
    center,
    zoom,
    points: track.points || [],
    vehicle: track.vehicle || '',
    visibility: track.visibility || 'public',
    editMode,
    isOwner: Boolean(track.isOwner),
    isCloseDetected,
  }
  
  const [socialImage, setSocialImage] = useState(null)
  const [socialImageUrl, setSocialImageUrl] = useState(null)
  const [doUploadImage, setDoUploadImage] = useState(null)
  const storageRef = firebase.storage().ref()
  const imagesRef = storageRef.child('lines/images')

  useEffect(() => {
    const hash = history.location.hash
    if (!hash.startsWith('#/')) return
    const matchedLineId = hash.replace("#/", "")
    pushPath(matchedLineId, false)
   }, [history])

  const shouldStartEdit = (() => {
    try {
      const params = new URLSearchParams(history.location.search || '')
      return params.get('edit') === '1'
    } catch (_) { return false }
  })()

 useEffect(() => {
   console.log("new line id", match)
    if(match && match.params.lineId
      && prevMatch && prevMatch.params.lineId
      && prevMatch.params.lineId == match.params.lineId) {
        return
    }

    if(ignoreMatch) {
      setIgnoreMatch(false)
      return
    }
    
    if(match && match.params.lineId) {
      setLineId(match.params.lineId)
      setEditMode(false)
    } 
    else {
      setLineId(null)
      setIsTool(false)
      setErrorMessage(null)
    }
  }, [match])

  // Create doc when line Id is not provided
  useEffect(() => {
    if(!lineId) return

    const unsubscribe = load()
    logRacingLineView()
    loadSocialImageURL(lineId)
    if(!match || match.params.lineId != lineId) pushPath(lineId)

    // Stop listening to changes
    return () => unsubscribe()
  }, [lineId, user])


  const pushPath = (lineId, setIgnore = true) => {
    let baseUrl = match ? match.params[0] : history.location.pathname
    if(baseUrl == "/") baseUrl = ""
    const path = baseUrl + "/r/" + lineId
    if(setIgnore) setIgnoreMatch(true)
    history.push(path)
  }

  const onDrawNewLine = async () => {
    setErrorMessage(null)
    setTrack(trackDefaults)
    setIsCloseDetected(false)
    setShowAccountPrompt(false)
    try {
      const created = await createFreeManualRacingLine({
        auth,
        track: {
          ...trackDefaults,
          name: props.name,
          placeId: props.placeId,
          url: props.url,
        },
      })
      setTrack({
        ...trackDefaults,
        visibility: created.requestedVisibility || trackDefaults.visibility,
        requestedVisibility: created.requestedVisibility || trackDefaults.visibility,
        publicationStatus: created.publicationStatus || 'draft',
      })
      setLineId(created.lineId)
      setEditMode(true)
    } catch (error) {
      if (error?.code === 'functions/resource-exhausted') {
        setSubscriptionContext({
          title: 'Five free racing lines used',
          message: 'Paddock Pass unlocks unlimited racing line creation.',
          intent: 'manual_line_limit',
        })
        setShowSubscriptionWall(true)
        return
      }
      setErrorMessage(error?.message || 'The racing line could not be created.')
    }
  }

  const onImportLine = async (points, user, telemetryId) => {
    console.log(`Received ${points.length} points from telemetry import`)

    // Convert telemetry points to our format
    const newPoints = points.map((point, index) => {
      // Determine point type based on inputs
      let type = 'coast' // default to coast/transition

      // If braking (brake > 0)
      if (point.brake > 0) {
        type = 'brake'
      }
      // If accelerating (throttle > 0 and not braking)
      else if (point.throttle > 0) {
        type = 'acc'
      }
      // For GoPro data without throttle/brake, use speed to determine type
      else if (!point.brake && !point.throttle && point.speed) {
        // This is likely GoPro data - we'll keep it as coast
        type = 'coast'
      }

      return {
        id: `telemetry-${index}`,
        lat: point.lat,
        lng: point.lng,
        altitude: point.alt || point.altitude || 0, // Include altitude for 3D visualization
        type,
        selected: false,
        speed: point.speed,
        rpm: point.rpm,
        gear: point.gear,
        throttle: point.throttle,
        brake: point.brake,
        dist: point.dist,
        pct: point.pct
      }
    })

    console.log(`First converted point:`, newPoints[0])

    // Create new track with imported points
    const newTrack = {
      ...trackDefaults,
      points: newPoints,
      // Set center to the first point (usually start/finish)
      lat: newPoints[0].lat,
      lng: newPoints[0].lng,
      telemetryId,
      // Set zoom to a reasonable level for viewing the track
      zoom: 17
    }

    setTrack(newTrack)
    setIsCloseDetected(false)
    setLineId(await createDoc(newTrack, user))
    setEditMode(false)
  }

  // On every change of the points
  useEffect(() => {
    if(track.points.length && editMode){ 
        firebase.analytics().logEvent('draw_point');
        // Autosave on every fifth point
        if(track.points.length % 5 === 0) save()
    }
  }, [track.points])

  const redoLast = () => {
    if (track.points.length === 0) return;
    const pointsCopy = track.points.slice();
    // Save current state to undo stack
    setUndoStack(prev => [...prev, track.points]);
    // Clear redo stack
    setRedoStack([]);
    // Remove last point
    pointsCopy.splice(-1, 1);
    setTrack(prev => ({...prev, points: pointsCopy}));
    firebase.analytics().logEvent('redo');
  };

  const duplicate = async user => {
    const trackWithoutLikes = {...track}
    trackWithoutLikes.likes = []
    setLineId(await createDoc(trackWithoutLikes, user))
    setTrack(trackWithoutLikes)
    setEditMode(true)
    firebase.analytics().logEvent('duplicate');
  }

  const done = async ({ throwOnError = false } = {}) => {
    try {
      await save()
      const currentUser = auth.app.auth().currentUser || user
      if (isCloseDetected && currentUser && !currentUser.isAnonymous) {
        await publishClosedRacingLines({ auth, lineId })
      }
      setDoUploadImage(true)
      setEditMode(false)
      if (isCloseDetected && currentUser?.isAnonymous) setShowAccountPrompt(true)
      firebase.analytics().logEvent('done', {lineId});
    } catch (error) {
      setErrorMessage(error?.message || 'The racing line could not be saved.')
      if (throwOnError) throw error
    }
  }

  const onSetLineId = lineId => {
    setLineId(lineId)
  }

  const onSetLine = line => {
    setLineId(line.id)
  }

  const onDelete = async () => {
    if(!window.confirm('Are you sure you wish to delete this racing line?')) return

    try {
      const doc = linesDb.doc(lineId)
      await doc.delete()

      setLineId(null)
      history.push(track.pathname)
      setEditMode(false)
      firebase.analytics().logEvent('delete');
      console.log("Racing line deleted: ", lineId)
    }
    catch(error) {
        console.error("Error deleting document: ", error);
    }
  }


  const createDoc = async (trackValues, currentUser) => {
    const { name, placeId, url } = props
    const t = trackValues
    try {
      const doc = linesDb.doc()

      // Clean points array - remove undefined values from each point
      const cleanedPoints = t.points.map(point => {
        const cleanPoint = {}

        // Always include required fields
        cleanPoint.id = point.id
        cleanPoint.lat = point.lat
        cleanPoint.lng = point.lng
        cleanPoint.type = point.type
        cleanPoint.selected = point.selected

        // Only add optional fields if they're defined
        if (point.speed !== undefined) cleanPoint.speed = point.speed
        if (point.altitude !== undefined) cleanPoint.altitude = point.altitude
        if (point.rpm !== undefined) cleanPoint.rpm = point.rpm
        if (point.gear !== undefined) cleanPoint.gear = point.gear
        if (point.throttle !== undefined) cleanPoint.throttle = point.throttle
        if (point.brake !== undefined) cleanPoint.brake = point.brake
        if (point.dist !== undefined) cleanPoint.dist = point.dist
        if (point.pct !== undefined) cleanPoint.pct = point.pct

        return cleanPoint
      })

      // Build document data, only including defined values
      const docData = {
          created: firebase.firestore.FieldValue.serverTimestamp(),
          location: new firebase.firestore.GeoPoint(+t.lat, +t.lng),
          zoom: t.zoom,
          points: cleanedPoints,
          user: currentUser.uid,
          likes: [],
          creationSource: lineSource(t).key
      }

      // Only add optional fields if they have defined values
      if (name !== undefined && name !== null) docData.name = name
      if (placeId !== undefined && placeId !== null) docData.placeId = placeId
      if (url !== undefined && url !== null) docData.url = url
      if (t.telemetryId !== undefined && t.telemetryId !== null) docData.telemetryId = t.telemetryId

      await doc.set(docData, { merge: true })

      console.log("Document created with id: ", doc.id)

      return doc.id
    }
    catch(error) {
        console.error("Error creating document: ", error);
        throw error
    }
  }

  const logRacingLineView = () => {
    firebase.analytics().logEvent('select_content', {
      content_type: 'racing line',
      content_id: lineId,
      items: [{ name: props.name }]
    });
    // Enforce monthly view cap for free users
    if (auth && auth.user && !auth.user.isAnonymous) {
      const trackView = firebase.functions ? firebase.functions() : null
      if (trackView && typeof trackView.httpsCallable === 'function') {
        try {
          const callable = trackView.httpsCallable('trackRacingLineView')
          callable({ lineId }).then((res) => {
            // If capped, show subscription wall
          }).catch((e) => {
            if (e && e.code === 'failed-precondition') {
              setSubscriptionContext({
                title: 'You’ve reached your monthly viewing limit',
                message: 'Free accounts can view up to 10 community racing lines per month. Subscribe to Paddock Pass to unlock unlimited viewing and more.',
                intent: 'community_view_limit'
              })
              setShowSubscriptionWall(true)
            }
          })
        } catch (e) {
          console.error('Failed to call trackRacingLineView', e)
        }
      }
    }
  }

  const load = () => {
    try {
        return linesDb.doc(lineId).onSnapshot(async doc => {
          if (!doc.exists) {
            setIsTool(false)
            setLineId(null)
            setEditMode(false)
            setErrorMessage("Racing line could not be found.")
            return
          }
          const data = doc.data()
          setIsCloseDetected(Boolean(data.isCloseDetected))
          const points = data.points
          if(!points) {
            setTrackStates({points: []})
            return
          }
        
          const location = data.location
          setTrack({
              ...data,
              lat: location.latitude,
              lng: location.longitude,
              zoom: data.zoom || track.zoom,
              pathname: data.url,
              visibility: data.publicationStatus === 'draft'
                ? (data.requestedVisibility || 'public')
                : (data.visibility || track.visibility),
              isOwner: user && user.uid == data.user,
              vehicle: data.vehicle || ""
          })
          // If requested via URL and owner, start in edit mode
          if (shouldStartEdit && user && user.uid == data.user) {
            setEditMode(true)
          }
          setIsTool(true)
          console.log("Points loaded from id: ", doc.id);
          const logEvent = props.embedLayout ? 'load_embed_line' : 'load_line'
          firebase.analytics().logEvent(logEvent);
        })
    }
    catch(error) {
        console.error("Error loading document: ", error);
    }
  }

  useEffect(() => {
    if (!lineId
      || track.publicationStatus !== 'draft'
      || track.isCloseDetected !== true
      || !user
      || user.isAnonymous) return

    publishClosedRacingLines({ auth, lineId }).catch(error => {
      console.error('Closed racing line remains private until publishing can be retried.', error)
      setErrorMessage('Your racing line is saved privately, but could not be published yet. Please try again.')
    })
  }, [lineId, track.publicationStatus, track.isCloseDetected, user])

  const uploadSocialImage = async socialImage => {
    const imageRef = imagesRef.child(`${lineId}/preview.png`);

    const snapshot = await imageRef.putString(socialImage, 'data_url')
    const downloadURL = await snapshot.ref.getDownloadURL()
    setSocialImageUrl(downloadURL)
    console.log("image uploaded")
  }

  const loadSocialImageURL = async lineId => {
    const candidates = [
      imagesRef.child(`${lineId}/preview.png`),
      imagesRef.child(`${lineId}.png`),
    ]
    for (const imageRef of candidates) {
      try {
        const url = await imageRef.getDownloadURL()
        setSocialImageUrl(url)
        return
      } catch (error) {
        if (error.code !== 'storage/object-not-found') {
          console.error("Error loading image: ", error)
          return
        }
      }
    }
    setSocialImageUrl(null)
  }

  const save = async () => {
    try {
      const doc = linesDb.doc(lineId)
      const visibilityFields = track.publicationStatus
        ? {
            visibility: track.publicationStatus === 'draft' ? 'private' : track.visibility,
            requestedVisibility: track.visibility,
          }
        : { visibility: track.visibility }
      await doc.set({
          points: track.points, 
          zoom,
          location: new firebase.firestore.GeoPoint(+center.lat, +center.lng),
          ...visibilityFields,
          isCloseDetected,
          vehicle: track.vehicle
      }, { merge: true });

      console.log("Points written to id: ", doc.id);
    }
    catch(error) {
        console.error("Error updating document: ", error);
        throw error
    }
    
    return true
  }

  const setTrackStates = states => {
    setTrack(prevState =>  ({ 
      ...prevState,
      ...states
    }))
  }

  const setVisibility = visibility => {
    setTrackStates({visibility})
  }

  const setPoints = (newPoints) => {
    // Save current state to undo stack before updating
    setUndoStack(prev => [...prev, track.points]);
    // Clear redo stack since we're creating a new branch of history
    setRedoStack([]);
    // Update track with new points
    setTrack(prev => ({...prev, points: newPoints}));
  };

  const setVehicle = vehicle => {
    setTrackStates({vehicle})
  }

  const setImage = image => {
    setSocialImage(image)
  }

  useEffect(() => {
    if(!socialImage || !doUploadImage) return
    
    uploadSocialImage(socialImage)
    setDoUploadImage(false)
  },[socialImage])
  
  const centerQuery = props.lat + "," + props.lng
  const query = props.placeId && props.placeId != "nan" ? "place_id:" + props.placeId + "&center=" + centerQuery : centerQuery;
  const src = `https://www.google.com/maps/embed/v1/place?q=${query}&maptype=satellite&zoom=${props.zoom}&key=${process.env.GATSBY_GMAPS_EMPED_API_KEY}`

  const renderLoader = () => <div className="lds-dual-ring"></div>

  const undo = () => {
    if (undoStack.length === 0) return;
    
    // Get the last state from undo stack
    const prevPoints = undoStack[undoStack.length - 1];
    // Add current state to redo stack
    setRedoStack(prev => [...prev, track.points]);
    // Restore previous state
    setTrack(prev => ({...prev, points: prevPoints}));
    // Remove the used state from undo stack
    setUndoStack(prev => prev.slice(0, -1));
    
    firebase.analytics().logEvent('undo');
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    
    // Get the last state from redo stack
    const nextPoints = redoStack[redoStack.length - 1];
    // Add current state to undo stack
    setUndoStack(prev => [...prev, track.points]);
    // Restore next state
    setTrack(prev => ({...prev, points: nextPoints}));
    // Remove the used state from redo stack
    setRedoStack(prev => prev.slice(0, -1));
    
    firebase.analytics().logEvent('redo');
  };

  editorActionsRef.current = {
    replacePoints: ({ points, isCloseDetected: closeDetected }) => {
      editorStateRef.current = { ...editorStateRef.current, points, isCloseDetected: closeDetected }
      setIsCloseDetected(closeDetected)
      setPoints(points)
    },
    appendPoints: ({ points, isCloseDetected: closeDetected }) => {
      const combined = [...(editorStateRef.current.points || []), ...points]
      editorStateRef.current = { ...editorStateRef.current, points: combined, isCloseDetected: closeDetected }
      setIsCloseDetected(closeDetected)
      setPoints(combined)
    },
    setDetails: details => {
      editorStateRef.current = { ...editorStateRef.current, ...details }
      if (details.vehicle !== undefined) setVehicle(details.vehicle)
      if (details.visibility !== undefined) setVisibility(details.visibility)
    },
    undo,
    redo,
    finish: () => done({ throwOnError: true }),
  }

  useEffect(() => {
    if (!isTool || !lineId || props.embedLayout || typeof document === 'undefined') {
      setWebMcpReady(false)
      return
    }
    const modelContext = document.modelContext
    if (!modelContext || typeof modelContext.registerTool !== 'function') {
      setWebMcpReady(false)
      return
    }
    const tools = createEditorTools({
      getState: () => editorStateRef.current,
      replacePoints: input => editorActionsRef.current.replacePoints(input),
      appendPoints: input => editorActionsRef.current.appendPoints(input),
      setDetails: input => editorActionsRef.current.setDetails(input),
      undo: () => editorActionsRef.current.undo(),
      redo: () => editorActionsRef.current.redo(),
      finish: () => editorActionsRef.current.finish(),
    })
    const controller = registerWebMcpTools(modelContext, tools)
    setWebMcpReady(Boolean(controller))
    controller?.ready?.catch(error => {
      console.warn('WebMCP editor tools could not be registered.', error)
      setWebMcpReady(false)
    })
    return () => controller?.abort()
  }, [isTool, lineId, props.embedLayout])

  const accountPromptUrl = typeof window === 'undefined'
    ? '/profile'
    : `/profile?redirect=${encodeURIComponent(`${window.location.pathname}${window.location.search}${window.location.hash}`)}`

  return (
    <>
      {!isTool && !props.embedLayout ?  
        <>
          <div className="row-full">
            <iframe
                id="embed-map"
                width="100%"
                height="400"
                frameBorder="0"
                referrerPolicy="origin"
                src = {src}
                />
          </div>
          {errorMessage && <span>{errorMessage}</span>}
        </>
        :
        <Suspense fallback={renderLoader()}>
          <>
              {webMcpReady && (
                <div className="fixed left-1/2 top-5 z-[9997] w-fit -translate-x-1/2">
                  <WebMcpInfo mode="editor" />
                </div>
              )}
              <LineTool 
              lineId={lineId} 
              editMode={editMode}
              setLineId={onSetLineId} 
              track={track}
              setPoints={setPoints}
              setCenter={setCenter}
              setZoom={setZoom}
              setEditMode={setEditMode}
              setIsCloseDetected={setIsCloseDetected}
              isCloseDetected={isCloseDetected}
              duplicate={() => requireRacingLineEntitlement({ auth, onAllowed: duplicate, onDenied: () => showCreationGate('duplicate this line', 'duplicate_line') })}
              done={done}
              redoLast={redoLast}
              delete={onDelete}
              setVisibility={setVisibility}
              setVehicle={setVehicle}
              setImage={setImage}
              socialImageUrl={socialImageUrl}
              showAccountPrompt={showAccountPrompt}
              accountPromptUrl={accountPromptUrl}
              dismissAccountPrompt={() => setShowAccountPrompt(false)}
              exit={() => {setLineId(null); setIsTool(false); setEditMode(false)}}
              undo={undo}
              canUndo={undoStack.length > 0}
              redo={redo}
              canRedo={redoStack.length > 0}
              {...props} />
          </>

        </Suspense>   

      }
       {!props.embedLayout && 
          <List
              setLine={onSetLine} 
               onNewLine={e => {
                 e.preventDefault();
                 onDrawNewLine()
              }}
              alwaysShowNewLine={true}
              oneRacingLinePerTrack={false}
              filterTrack={props.name}
               showLike={true}
               onImport={({ points, carInfo, telemetryId } = {}) => {
                 requireCreationEntitlement(
                   user => onImportLine(points, user, telemetryId),
                   'import telemetry',
                   'telemetry_import'
                 )
               }}
              limit={100}/>}
      {showSubscriptionWall && (
        <SubscriptionWall 
          onClose={() => setShowSubscriptionWall(false)}
          title={subscriptionContext.title}
          message={subscriptionContext.message}
          intent={subscriptionContext.intent}
        />
      )}
          
      
    </>
  )
};

export default App;
